import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import React, { useEffect } from 'react';

const Footer = () => {
    const bookAppointment1Ref = React.useRef();

    useEffect(() => {
        // Continuous jumping animation for buttons
        const jumpAnimation1 = gsap.to(bookAppointment1Ref.current, {
            y: -10,
            duration: 0.8,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
        });

        return () => {
            jumpAnimation1.kill();
        };
    }, []);

    return (
        <footer className="bg-gray-900 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8">
                    {/* Left Side - Navigation & Contact Info */}
                    <div className="space-y-8">
                        {/* Navigation Links */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-teal-400">Quick Links</h3>
                            <nav className="flex flex-wrap gap-x-8 gap-y-2">
                                <Link 
                                    to="/" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    Home
                                </Link>
                                <Link 
                                    to="/about" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    About us
                                </Link>
                                <Link 
                                    to="/treatments" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    Treatments
                                </Link>
                                <Link 
                                    to="/procedures-offered" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    Procedures offered
                                </Link>
                                <Link 
                                    to="/contact" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    Contact
                                </Link>
                                </nav>
                        </div>

                        {/* Contact Information */}
                        <div className="space-y-4 flex gap-5">
                            <div>
                                <h3 className="text-lg font-bold text-teal-400 mb-4">Contact Us</h3>
                            <address className="not-italic space-y-2 text-gray-300">
                                <p className="text-lg font-medium text-white">ADDRESS :</p>
                                <p>326, 3rd floor, Golden Plaza,</p>
                                <p>Amar Javan circle near Burger 🍔 King,</p>
                                <p> S.P Ring road, Nikol,</p>
                                <p>Ahmedabad-380013</p>
                                
                                <div className="pt-2 space-y-1">
                                    <p className="font-medium text-white text-lg">Phone: <a href="tel:+917319030666" className="hover:text-teal-400">+91 7319030666</a></p>
                                    <p className="font-medium text-white text-lg">Email: <a href="mailto:drnidhi96estheelite@gmail.com" className="hover:text-teal-400">drnidhi96estheelite@gmail.com</a></p>
                                    <p className="font-medium text-white text-lg">Instagram Handle: <a href="https://www.instagram.com/esthe_elitebydr.nidhi?igsh=MW1jdmppNjdoeXNybQ==" className="hover:text-teal-400">esthe_elitebydr.nidhi</a></p>
                                </div>
                            </address>
                            </div>
                            <div className=" h-[200px] w-[200px] rounded-lg overflow-hidden shadow-md">
                   <motion.div
  initial={{ opacity: 0, y: 40 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.5, type: "spring", stiffness: 80 }}
  viewport={{ once: true }}
  className="relative group overflow-hidden rounded-3xl bg-white bg-opacity-10 border border-white/30 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.2)] transition-all w-full h-96"
>
  <a 
  href="https://maps.app.goo.gl/CkQcDE4T4PiTEoEW6"
  target="_blank" 
  rel="noopener noreferrer"
  className="w-full h-full block relative group rounded-3xl overflow-hidden"
>
  <img 
    src="/Images/location.webp" 
    alt="Clinic Location" 
    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105 group-hover:rotate-1"
    loading="lazy"
  />

  {/* Overlay gradient with darker base for readability */}
  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent flex flex-col justify-end p-5">
    <h4 
      className="text-white text-2xl font-bold mb-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      Visit Us
    </h4>
    <p 
      className="text-white/90 text-sm leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
    >
      326, Golden Plaza<br />
      Amar Javan Circle, Nikol<br />
      Ahmedabad - 380013
    </p>
  </div>
  
</a>


  <motion.div 
    className="absolute inset-0 rounded-3xl pointer-events-none transition-all duration-500"
    whileHover={{
      boxShadow: '0 0 40px rgba(0, 255, 200, 0.4), 0 0 80px rgba(0, 255, 200, 0.2)'
    }}
  />
</motion.div>


                    </div>
                        </div>
                        
                    </div>
                    


                    {/* Right Side - CTA Section */}
                    <div className="bg-teal-600 rounded-xl p-8 lg:p-10">
                        <motion.div 
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={{
                                hidden: {},
                                visible: {
                                    transition: {
                                        staggerChildren: 0.1
                                    }
                                }
                            }}
                            className="text-center h-full flex flex-col justify-center"
                        >
                            <motion.h2 
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
                                }}
                                className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6"
                                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                            >
                                Ready for Healthier Skin?
                            </motion.h2>
                            
                            <motion.p 
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.1 } }
                                }}
                                className="text-base sm:text-lg max-w-md mx-auto mb-6 sm:mb-8 text-teal-100"
                                style={{ fontFamily: "'Montserrat', sans-serif" }}
                            >
                                Schedule your consultation today and take the first step toward your skin health journey.
                            </motion.p>
                            
                            <motion.div 
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } }
                                }}
                                className="flex flex-wrap justify-center gap-3 sm:gap-4"
                            >
                                <Link 
                                    to="/contact" 
                                    ref={bookAppointment1Ref}
                                    className="px-6 sm:px-8 py-2 sm:py-3 bg-white text-teal-700 hover:bg-teal-50 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg text-sm sm:text-base"
                                    style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
                                >
                                    Book an Appointment
                                </Link>
                                <Link 
                                    to="/treatments" 
                                    className="px-6 sm:px-8 py-2 sm:py-3 border-2 border-white text-white hover:bg-teal-700 hover:border-teal-700 rounded-lg font-medium transition-colors hover:shadow-md text-sm sm:text-base"
                                    style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
                                >
                                    Explore Treatments
                                </Link>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>

                {/* Copyright/Bottom Row */}
                <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
                    <p>© {new Date().getFullYear()} Dr. Nidhi Patel | Designed by Daksh Patel</p>
                </div>
            </div>
        </footer>
    );
}

export default Footer;